/**
 * Typed API helpers derived from the FastAPI OpenAPI schema.
 *
 * Usage:
 *   import { typedApi } from './api/typed';
 *   const data = await typedApi.get('/api/commitments');           // list
 *   const one  = await typedApi.get(`/api/commitments/${id}`);     // dynamic
 *
 * Dynamic paths are matched against their OpenAPI parameterised equivalents
 * (e.g. `/api/commitments/{commitment_id}`) via template-literal types, so
 * typos like `/api/commitmnts/...` still fail at compile time.
 *
 * The underlying axios instance (`api` from `./api`) still works as before
 * and keeps `withCredentials: true`. This helper layers types on top; it
 * does NOT replace the axios calls.
 *
 * To regenerate the schema after backend changes:
 *   yarn regen:api-types
 */
import api from '../api';
import type { paths, components } from './generated';

/** Map of every documented API path -> the HTTP method shape. */
export type ApiPaths = paths;

/**
 * All Pydantic models exposed by the backend, indexed by name.
 *   TS:     type Task = Schemas['TaskModel'];
 *   JSDoc:  @type {import('./api/typed').Schemas['TaskModel']}
 */
export type Schemas = components['schemas'];

/**
 * Convert an OpenAPI path pattern into a template-literal type that accepts
 * any string substitution for {param} placeholders.
 *   `/api/commitments/{commitment_id}` -> `/api/commitments/${string}`
 */
type ConcretePath<P extends string> =
  P extends `${infer A}{${string}}${infer B}`
    ? `${A}${string}${ConcretePath<B>}`
    : P;

/**
 * Given a concrete URL like `/api/commitments/abc123`, find the OpenAPI path
 * key (`/api/commitments/{commitment_id}`) whose template matches. The method
 * helpers below use this to resolve response/request body types.
 */
type MatchPath<S extends string, Keys extends keyof paths = keyof paths> =
  Keys extends string ? (S extends ConcretePath<Keys> ? Keys : never) : never;

/** The set of all valid input strings (both literal and interpolated). */
type AnyPath = { [K in keyof paths]: K extends string ? ConcretePath<K> : never }[keyof paths];

type OkJson<Op> = Op extends { responses: { 200: { content: { 'application/json': infer T } } } } ? T : unknown;
type ReqJson<Op> = Op extends { requestBody: { content: { 'application/json': infer T } } } ? T : undefined;
type PathOp<P extends keyof paths, M extends keyof paths[P]> = paths[P][M];

/**
 * Strip the leading `/api` because the underlying axios instance already has
 * it in the baseURL.
 */
function stripApi(path: string): string {
  return path.startsWith('/api') ? path.slice(4) || '/' : path;
}

type GetConfig = Parameters<typeof api.get>[1];
type PostConfig = Parameters<typeof api.post>[2];

export async function typedGet<S extends AnyPath>(
  path: S,
  config?: GetConfig,
): Promise<MatchPath<S> extends never ? unknown : OkJson<PathOp<MatchPath<S>, 'get'>>> {
  const res = await api.get(stripApi(path), config);
  return res.data;
}

export async function typedPost<S extends AnyPath>(
  path: S,
  body: MatchPath<S> extends never ? unknown : ReqJson<PathOp<MatchPath<S>, 'post'>> | unknown,
  config?: PostConfig,
): Promise<MatchPath<S> extends never ? unknown : OkJson<PathOp<MatchPath<S>, 'post'>>> {
  const res = await api.post(stripApi(path), body, config);
  return res.data;
}

export async function typedPut<S extends AnyPath>(
  path: S,
  body: MatchPath<S> extends never ? unknown : ReqJson<PathOp<MatchPath<S>, 'put'>> | unknown,
  config?: PostConfig,
): Promise<MatchPath<S> extends never ? unknown : OkJson<PathOp<MatchPath<S>, 'put'>>> {
  const res = await api.put(stripApi(path), body, config);
  return res.data;
}

export async function typedDelete<S extends AnyPath>(
  path: S,
  config?: GetConfig,
): Promise<MatchPath<S> extends never ? unknown : OkJson<PathOp<MatchPath<S>, 'delete'>>> {
  const res = await api.delete(stripApi(path), config);
  return res.data;
}

export const typedApi = {
  get: typedGet,
  post: typedPost,
  put: typedPut,
  delete: typedDelete,
};
